import React, { useState, useEffect } from 'react';
import { FiX, FiSave, FiTrash2 } from 'react-icons/fi';
import axios from 'axios';
import { useAuth } from '../../../contexts/AuthContext';

interface PaymentEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  payment: Payment | null;
  onSave: (updatedPayment: Payment) => void;
  onDelete: (paymentId: string) => void;
  loading?: boolean;
  error?: string;
}

interface Payment {
  id: string;
  amount: number;
  date: string;
  method: 'cash' | 'check' | 'card' | 'bank_transfer' | 'zelle';
  reference: string;
  memo: string;
  status: 'active' | 'voided';
  customer_id: string;
  invoice_numbers?: string;
}

interface PaymentForm {
  amount: number;
  date: string;
  method: 'cash' | 'check' | 'card' | 'bank_transfer' | 'zelle';
  reference: string;
  memo: string;
}

interface InvoiceOption {
  id: string;
  number: string;
  balance_due: number;
  status: string;
}

interface PaymentApplication {
  invoice_id: string;
  amount: number;
  invoice_number?: string;
  current_balance?: number;
}

const PaymentEditDrawer: React.FC<PaymentEditDrawerProps> = ({
  isOpen,
  onClose,
  payment,
  onSave,
  onDelete,
  loading = false,
  error = ''
}) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const [form, setForm] = useState<Partial<Payment>>({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState('');
  const [invoices, setInvoices] = useState<InvoiceOption[]>([]);
  const [applications, setApplications] = useState<PaymentApplication[]>([]);
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [allocError, setAllocError] = useState('');

  useEffect(() => {
    if (payment) {
      const normalizedDate = payment.date && typeof payment.date === 'string'
        ? (payment.date.includes('T') ? payment.date.split('T')[0] : payment.date)
        : '';
      setForm({
        amount: payment.amount,
        date: normalizedDate,
        method: payment.method,
        reference: payment.reference || '',
        memo: payment.memo || ''
      });
      // reset allocations state
      setAllocations({});
      setApplications([]);
      setInvoices([]);
      setAllocError('');
    }
  }, [payment]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isOpen || !payment || !currentBusiness) return;
      const headers: any = { };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      try {
        // fetch unpaid invoices for this customer
        const invRes = await axios.get(`${API_BASE_URL}/invoices?customer_id=${payment.customer_id}&status=open,overdue,partial_open,partial_over`, {
          headers,
          withCredentials: isCookieMode,
        });
        const invs: InvoiceOption[] = (invRes.data.invoices || []).map((i: any) => ({ id: i.id, number: i.number, balance_due: i.balance_due, status: i.status }));
        // fetch customer to include starting balance as pseudo-invoice if > 0
        const custRes = await axios.get(`${API_BASE_URL}/customers/${payment.customer_id}`, {
          headers,
          withCredentials: isCookieMode,
        });
        const sb = Number(custRes.data?.starting_balance || 0);
        if (sb > 0) {
          invs.unshift({ id: 'starting-balance', number: 'STARTING ACCOUNT BALANCE', balance_due: sb, status: 'overdue' } as any);
        } else {
          // No raw starting_balance; try to include SBAL invoice if it exists
          try {
            const sbalNumber = `SBAL-${payment.customer_id}`;
            const sbalRes = await axios.get(`${API_BASE_URL}/invoices/by-number/${encodeURIComponent(sbalNumber)}`, {
              headers,
              withCredentials: isCookieMode,
            });
            const sbal = sbalRes.data;
            if (sbal && !invs.find((inv: any) => String(inv.id) === String(sbal.id))) {
              invs.unshift({ id: sbal.id, number: sbal.number, balance_due: sbal.balance_due, status: sbal.status } as any);
            }
          } catch (e) {
            // 404 or other: SBAL not present; silently ignore
          }
        }
        setInvoices(invs);
        // fetch current payment applications
        const appsRes = await axios.get(`${API_BASE_URL}/payments/${payment.id}/applications`, {
          headers,
          withCredentials: isCookieMode,
        });
        const apps: PaymentApplication[] = appsRes.data || [];
        setApplications(apps);
        // seed allocations with current apps
        const seed: Record<string, number> = {};
        for (const a of apps) seed[a.invoice_id] = a.amount;
        setAllocations(seed);
      } catch (e: any) {
        setAllocError(e.response?.data?.error || 'Failed to load payment allocations');
      }
    };
    fetchData();
  }, [isOpen, payment, currentBusiness, isCookieMode, token, API_BASE_URL]);

  const handleInputChange = (field: keyof Payment, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!payment || !currentBusiness) return;
    if (!isCookieMode && !token) return;

    setSaving(true);
    setFormError('');

    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const response = await axios.put(`${API_BASE_URL}/payments/${payment.id}`, {
        amount: form.amount,
        date: form.date,
        method: form.method,
        reference: form.reference,
        memo: form.memo
      }, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });

      // Check if reallocation occurred
      if (response.data.reallocation) {
        const { oldAmount, newAmount } = response.data.reallocation;
        if (oldAmount !== newAmount) {
          alert(`Payment reallocated successfully!\nAmount changed from $${oldAmount} to $${newAmount}\nInvoice balances have been updated accordingly.`);
        }
      }

      onSave(response.data);
      onClose();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to update payment');
    } finally {
      setSaving(false);
    }
  };

  const totalAllocated = Object.values(allocations).reduce((s, v) => s + (Number.isFinite(v) ? Number(v) : 0), 0);
  const remainingToAllocate = Math.max(0, (Number(form.amount || 0)) - totalAllocated);

  const handleAllocationChange = (invoiceId: string, value: number) => {
    setAllocations(prev => ({ ...prev, [invoiceId]: value }));
  };

  const handleSaveAllocations = async () => {
    if (!payment || !currentBusiness) return;
    if (!isCookieMode && !token) return;
    setAllocError('');
    try {
      const headers: any = { };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const payload = {
        allocations: Object.entries(allocations)
          .filter(([, amt]) => (Number(amt) || 0) > 0)
          .map(([invoice_id, amount]) => ({ invoice_id, amount: Number(amount) }))
      };
      await axios.put(`${API_BASE_URL}/payments/${payment.id}/applications`, payload, {
        headers,
        withCredentials: isCookieMode,
      });
      // After saving, refresh apps and close
      onSave(payment);
      onClose();
    } catch (e: any) {
      setAllocError(e.response?.data?.error || 'Failed to save allocations');
    }
  };

  const handleDelete = async () => {
    if (!payment || !currentBusiness) return;
    if (!isCookieMode && !token) return;

    if (!window.confirm('Are you sure you want to void this payment? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    setFormError('');

    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      await axios.delete(`${API_BASE_URL}/payments/${payment.id}`, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
        data: { reason: 'Voided by user' }
      });

      // Call onDelete to refresh data and onClose to close the drawer
      onDelete(payment.id);
      onClose();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to void payment');
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen || !payment) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      <div className="absolute right-0 top-0 h-full w-full sm:w-[560px] md:w-[600px] max-w-[640px] bg-white dark:bg-neutral-900 shadow-xl">
        <div className="flex h-full flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200 dark:border-neutral-700">
            <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50">
              Edit Payment
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md"
            >
              <FiX className="text-neutral-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-800 dark:text-red-200 text-sm">{error}</p>
              </div>
            )}

            {formError && (
              <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-red-800 dark:text-red-200 text-sm">{formError}</p>
              </div>
            )}

            {/* Payment Info */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                Payment Information
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.amount || ''}
                    onChange={(e) => handleInputChange('amount', parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    disabled={payment.status === 'voided'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Date
                  </label>
                  <input
                    type="date"
                    value={form.date || ''}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    disabled={payment.status === 'voided'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Method
                  </label>
                  <select
                    value={form.method || ''}
                    onChange={(e) => handleInputChange('method', e.target.value)}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    disabled={payment.status === 'voided'}
                  >
                    <option value="cash">Cash</option>
                    <option value="check">Check</option>
                    <option value="card">Card</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="zelle">Zelle</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Reference
                  </label>
                  <input
                    type="text"
                    value={form.reference || ''}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    placeholder="Check #, transaction ID, etc."
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    disabled={payment.status === 'voided'}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                    Memo
                  </label>
                  <textarea
                    value={form.memo || ''}
                    onChange={(e) => handleInputChange('memo', e.target.value)}
                    placeholder="Additional notes..."
                    rows={3}
                    className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100"
                    disabled={payment.status === 'voided'}
                  />
                </div>
              </div>
            </div>

            {/* Manual Allocation Editor */}
            {payment.status === 'active' && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-3">
                  Allocate Payment to Invoices
                </h3>
                {allocError && (
                  <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-sm text-red-700 dark:text-red-300">
                    {allocError}
                  </div>
                )}
                <div className="space-y-2">
                  {invoices.length === 0 && (
                    <div className="text-sm text-neutral-600 dark:text-neutral-300">No unpaid invoices.</div>
                  )}
                  {invoices.map(inv => (
                    <div key={inv.id} className="flex items-center justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-neutral-800 dark:text-neutral-100">#{inv.number}</div>
                        <div className="text-xs text-neutral-500">Balance due: ${inv.balance_due.toFixed(2)} • {inv.status}</div>
                      </div>
                      <div className="shrink-0">
                        <input
                          type="number"
                          step="0.01"
                          value={allocations[inv.id] ?? 0}
                          onChange={(e) => handleAllocationChange(inv.id, parseFloat(e.target.value) || 0)}
                          className="w-28 px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 text-right"
                          disabled={payment.status === 'voided'}
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 text-xs text-neutral-600 dark:text-neutral-300">
                  Total allocated: ${totalAllocated.toFixed(2)} / Amount: ${Number(form.amount || 0).toFixed(2)} {remainingToAllocate > 0 ? `(Remaining: $${remainingToAllocate.toFixed(2)})` : ''}
                </div>
                <div className="mt-3">
                  <button
                    onClick={handleSaveAllocations}
                    className="px-3 py-2 bg-neutral-800 dark:bg-neutral-200 text-white dark:text-neutral-900 rounded-md hover:opacity-90"
                    disabled={saving}
                  >
                    Save Allocations
                  </button>
                </div>
              </div>
            )}

            {/* Status */}
            <div className="mb-6">
              <h3 className="text-sm font-medium text-neutral-600 dark:text-neutral-400 mb-2">
                Status
              </h3>
              <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                payment.status === 'active' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
              }`}>
                {payment.status === 'active' ? 'Active' : 'Voided'}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-neutral-200 dark:border-neutral-700">
            <div className="flex space-x-3">
              {payment.status === 'active' && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors disabled:opacity-50"
                >
                  <FiTrash2 className="inline mr-2" />
                  {deleting ? 'Voiding...' : 'Void Payment'}
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-md transition-colors"
              >
                Cancel
              </button>
              
              {payment.status === 'active' && (
                <button
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-4 py-2 bg-brand text-white rounded-md hover:bg-brand/90 transition-colors disabled:opacity-50"
                >
                  <FiSave className="inline mr-2" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentEditDrawer; 